import { AuthProvider } from "config/auth/AuthProvider";
import PrivateRoute from "config/routes/PrivateRoute";
import { lazy, Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router-dom";
import LoadingPage from "views/Base/LoadingPage";
import Element from "./Element";

const Page404 = lazy(() => import("views/Base/Page404"));
const AccountingFirmsList = lazy(() => import("views/AccountingFirm/AccountingFirmsList"));
const EntityTableCard = lazy(() => import("components/EntityTableCard"));
const AccountingFirmsAdd = lazy(() => import("views/AccountingFirm/AddAccountingFirm"));
const AccountingFirmsDetail = lazy(() => import("views/AccountingFirm"));
const EditUser = lazy(() => import("views/Collaborators/components/EditUser"));
const AddUser = lazy(() => import("views/Collaborators/components/AddUser"));
const UsersList = lazy(() => import("views/Collaborators"));
const CompaniesAdd = lazy(() => import("views/Company/CompaniesAdd"));
const CompaniesDetail = lazy(() => import("views/Company/index"));
const CompaniesList = lazy(() => import("views/Company/CompaniesList"));
const Highligths = lazy(() => import("views/Home/NewsFeed/Highligths"));
const ListArticles = lazy(() => import("views/Home/NewsFeed/ListArticles"));
const Article = lazy(() => import("views/Home/NewsFeed/Article"));
const Home = lazy(() => import("views/Home/"));
const Profile = lazy(() => import("views/Profile"));
const BankAccounts = lazy(() => import("views/Company/General/Banks/BankAccounts"));
const BankAccountForm = lazy(
  () => import("views/Company/General/Banks/components/BankAccountForm"),
);

const routers = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={
        <AuthProvider>
          <PrivateRoute />
        </AuthProvider>
      }
    >
      <Route
        path="/"
        element={
          <Element>
            <Home />
          </Element>
        }
      >
        <Route path="company/:companyId" element={<Highligths />} />

        <Route
          path="articles/:categoryId/:articleId"
          element={
            <Element>
              <Article />
            </Element>
          }
        />
        <Route
          path="articles/:categoryId"
          element={
            <Element>
              <ListArticles />
            </Element>
          }
        />
      </Route>
      <Route
        path="users"
        element={
          <Element requiredPermission="displayUsers">
            <EntityTableCard />
          </Element>
        }
      >
        <Route
          index
          element={
            <Element requiredPermission="displayUsers">
              <UsersList />
            </Element>
          }
        />
        <Route
          path="add"
          element={
            <Element requiredPermission="createUsers">
              <AddUser />
            </Element>
          }
        />
      </Route>

      <Route path="users">
        <Route path=":userId" element={<Navigate to="edit/info" replace />} />
        <Route path=":userId/edit" element={<Navigate to="info" replace />} />
        <Route
          path=":userId/edit/:menu"
          element={
            <Element requiredPermission="updateUsers">
              <EditUser />
            </Element>
          }
        />
      </Route>
      <Route path="account">
        <Route index element={<Navigate to={`/account/profile`} replace />} />
        <Route
          path=":menu"
          element={
            <Element requiredPermission="updateProfile">
              <Profile />
            </Element>
          }
        />
      </Route>
      <Route
        path="accounting-firms"
        element={
          <Element requiredPermission="displayAccountingFirms">
            <EntityTableCard />
          </Element>
        }
      >
        <Route
          index
          element={
            <Element requiredPermission="displayAccountingFirms">
              <AccountingFirmsList />
            </Element>
          }
        />
      </Route>
      <Route path="accounting-firms">
        <Route
          path="add"
          element={
            <Element requiredPermission="createAccountingFirms">
              <AccountingFirmsAdd />
            </Element>
          }
        />
        <Route
          path=":accountingFirmId/:menu"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AccountingFirmsDetail />
            </Suspense>
          }
        >
          <Route
            path=":bankId/bank-accounts"
            element={
              <Suspense fallback={<LoadingPage />}>
                <BankAccounts />
              </Suspense>
            }
          />
          <Route
            path=":subMenu"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AccountingFirmsDetail />
              </Suspense>
            }
          />
        </Route>
      </Route>

      <Route
        path="companies"
        element={
          <Element requiredPermission="displayCompanies">
            <EntityTableCard />
          </Element>
        }
      >
        <Route
          index
          element={
            <Element requiredPermission="displayCompanies">
              <CompaniesList />
            </Element>
          }
        />
      </Route>
      <Route path="companies">
        <Route
          path="add"
          element={
            <Element requiredPermission="createCompanies">
              <CompaniesAdd />
            </Element>
          }
        />
        <Route
          path=":companyId"
          element={
            <Element requiredPermission="displayCompanies">
              <CompaniesDetail />
            </Element>
          }
        />
        <Route
          path=":companyId/:menu"
          element={
            <Suspense fallback={<LoadingPage />}>
              <CompaniesDetail />
            </Suspense>
          }
        >
          <Route
            path=":subMenu"
            element={
              <Suspense fallback={<LoadingPage />}>
                <CompaniesDetail />
              </Suspense>
            }
          >
            <Route path=":bankId/bank-accounts">
              <Route
                index
                element={
                  <Suspense fallback={<LoadingPage />}>
                    <BankAccounts />
                  </Suspense>
                }
              />
              <Route
                path=":bankAccountId"
                element={
                  <Element requiredPermission="canUpdateBankAccount">
                    <BankAccountForm />
                  </Element>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route
        path="store-token"
        element={
          <Element>
            <Home />
          </Element>
        }
      />
      <Route
        path="*"
        element={
          <Suspense fallback={<LoadingPage />}>
            <Page404 />
          </Suspense>
        }
      />
    </Route>,
  ),
);

export { routers };
